// Creating a project: the details forms (the New Project dialog and the first
// project form on the project list) collect name, description and region, then
// the plan decides what happens. Free creates the project right away; Standard
// and Premium go to checkout, and the billing ticket creates the project once
// the payment succeeds (see createSubscription in subscription/subscription.ts).
import type { Router } from "vue-router";
import { type ServiceFromServer, createService } from "@/code/service";
import { addServiceToList } from "@/views/project-list";
import { createSubscription } from "@/views/subscription/subscription";

export type NewProjectPlan = "free" | "standard" | "premium";

export const NEW_PROJECT_PLANS: { plan: NewProjectPlan; label: string }[] = [
    { plan: "free", label: "Free" },
    { plan: "standard", label: "Standard" },
    { plan: "premium", label: "Premium" },
];

export const planLabel = (plan: NewProjectPlan | "") => NEW_PROJECT_PLANS.find((p) => p.plan === plan)?.label || "";

export interface PendingProject {
    name: string;
    description: string;
    /** Empty: the region closest to the visitor, as the picker preselects. */
    region: string;
}

// The details carried from the form to the plan page, and back from a cancelled
// checkout. Session storage, so a reload of the plan page keeps them; kept in
// memory too for a browser that refuses storage.
const PENDING_KEY = "skapi:new_project";
let pendingInMemory: PendingProject | null = null;

export function savePendingProject(p: PendingProject) {
    pendingInMemory = { ...p };
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingInMemory));
    } catch {
        // storage blocked: memory only
    }
}

export function loadPendingProject(): PendingProject | null {
    if (pendingInMemory) return { ...pendingInMemory };
    try {
        const raw = sessionStorage.getItem(PENDING_KEY);
        const p = raw ? JSON.parse(raw) : null;
        if (p && typeof p.name === "string" && p.name.trim()) {
            pendingInMemory = {
                name: p.name,
                description: typeof p.description === "string" ? p.description : "",
                region: typeof p.region === "string" ? p.region : "",
            };
            return { ...pendingInMemory };
        }
    } catch {
        // unreadable: no pending project
    }
    return null;
}

export function clearPendingProject() {
    pendingInMemory = null;
    try {
        sessionStorage.removeItem(PENDING_KEY);
    } catch {
        // storage blocked
    }
}

/** The name rules the backend applies. Empty string when the name is fine. */
export function projectNameProblem(name: string): string {
    const n = (name || "").trim();
    if (!n) return "Enter a project name.";
    if (n.length > 256) return "A project name is at most 256 characters.";
    if (/[<>`]/.test(n)) return "Angle brackets and backticks are not allowed";
    return "";
}

/** Free: create the project now and open it. */
export async function createFreeProject(p: PendingProject, router: Router) {
    const params: { name: string; description?: string; region?: string } = { name: p.name.trim() };
    if (p.description.trim()) params.description = p.description.trim();
    if (p.region) params.region = p.region;

    const service = (await createService(params)) as ServiceFromServer;
    clearPendingProject();
    try {
        await addServiceToList([service]);
    } catch {
        // The project page pulls it by id when the list could not take it.
    }
    router.push(`/my-projects/${service.service}`);
}

/**
 * Standard or Premium: leave for checkout. The page stays on its loading state
 * until the browser navigates away; a failed session start resolves without
 * navigating (createSubscription shows the reason).
 */
export async function checkoutNewProject(plan: "standard" | "premium", p: PendingProject) {
    savePendingProject(p);
    await createSubscription({
        ticket_id: plan,
        new_project: {
            name: p.name.trim(),
            description: p.description.trim(),
            region: p.region,
        },
    });
}

/** What a details form does on submit: the plan page, or the preselected plan. */
export async function continueNewProject(p: PendingProject, plan: NewProjectPlan | "", router: Router) {
    savePendingProject(p);
    if (plan === "free") return createFreeProject(p, router);
    if (plan === "standard" || plan === "premium") return checkoutNewProject(plan, p);
    router.push({ name: "create" });
}
